/**
 * ObstaclePlacementPreview — Ghost mesh following cursor during obstacle placement
 *
 * Renders a semi-transparent preview of the obstacle at the cursor position.
 * Uses a single Three.js mesh (box or cylinder) added directly to the scene.
 */

import { useEffect, useRef } from 'react'
import * as THREE from 'three'
import { useObstacleStore } from '@/stores/obstacleStore'
import { FEET_TO_METERS } from '@/constants/units'

const GHOST_COLOR = 0x4488ff
const GHOST_OPACITY = 0.35

interface ObstaclePlacementPreviewProps {
  scene: THREE.Scene | null
  map: mapboxgl.Map | null
}

export function ObstaclePlacementPreview({ scene, map }: ObstaclePlacementPreviewProps) {
  const placingPreset = useObstacleStore((s) => s.placingPreset)
  const creatingDefaults = useObstacleStore((s) => s.creatingDefaults)
  const previewPosition = useObstacleStore((s) => s.previewPosition)
  const meshRef = useRef<THREE.Mesh | null>(null)
  const wireRef = useRef<THREE.LineSegments | null>(null)

  // Unified dimension source: preset (edit-mode placement) OR creatingDefaults (view-mode creation)
  const dims = placingPreset ?? creatingDefaults

  // Create/update mesh when dimensions change
  useEffect(() => {
    if (!scene || !dims) return

    // Clean up previous
    if (meshRef.current) {
      scene.remove(meshRef.current)
      meshRef.current.geometry.dispose()
      ;(meshRef.current.material as THREE.Material).dispose()
      meshRef.current = null
    }
    if (wireRef.current) {
      scene.remove(wireRef.current)
      wireRef.current.geometry.dispose()
      ;(wireRef.current.material as THREE.Material).dispose()
      wireRef.current = null
    }

    let geometry: THREE.BufferGeometry

    if (dims.shape === 'circle' && dims.radius != null) {
      const r = dims.radius * FEET_TO_METERS
      const h = dims.height * FEET_TO_METERS
      geometry = new THREE.CylinderGeometry(r, r, h, 16)
    } else {
      const w = (dims.width ?? 4) * FEET_TO_METERS
      const d = (dims.depth ?? 4) * FEET_TO_METERS
      const h = dims.height * FEET_TO_METERS
      geometry = new THREE.BoxGeometry(w, h, d)
    }

    const material = new THREE.MeshStandardMaterial({
      color: GHOST_COLOR,
      transparent: true,
      opacity: GHOST_OPACITY,
      depthWrite: false,
    })

    const mesh = new THREE.Mesh(geometry, material)
    mesh.name = 'obstacle-placement-preview'
    mesh.visible = false // Hidden until we get a position
    scene.add(mesh)
    meshRef.current = mesh

    // Wireframe overlay for visibility
    const edgesGeo = new THREE.EdgesGeometry(geometry)
    const edgesMat = new THREE.LineBasicMaterial({ color: GHOST_COLOR, transparent: true, opacity: 0.7 })
    const wireframe = new THREE.LineSegments(edgesGeo, edgesMat)
    wireframe.name = 'obstacle-placement-preview-wire'
    wireframe.visible = false
    scene.add(wireframe)
    wireRef.current = wireframe

    return () => {
      if (meshRef.current) {
        scene.remove(meshRef.current)
        meshRef.current.geometry.dispose()
        ;(meshRef.current.material as THREE.Material).dispose()
        meshRef.current = null
      }
      if (wireRef.current) {
        scene.remove(wireRef.current)
        wireRef.current.geometry.dispose()
        ;(wireRef.current.material as THREE.Material).dispose()
        wireRef.current = null
      }
    }
  }, [scene, dims])

  // Update position when cursor moves
  useEffect(() => {
    if (!meshRef.current || !wireRef.current || !dims) return

    if (!previewPosition) {
      meshRef.current.visible = false
      wireRef.current.visible = false
      return
    }

    const xMeters = previewPosition.x * FEET_TO_METERS
    const zMeters = previewPosition.z * FEET_TO_METERS
    const elevation = (dims.elevation ?? 0) * FEET_TO_METERS
    const halfHeight = (dims.height * FEET_TO_METERS) / 2

    meshRef.current.position.set(xMeters, elevation + halfHeight, zMeters)
    meshRef.current.visible = true

    wireRef.current.position.set(xMeters, elevation + halfHeight, zMeters)
    wireRef.current.visible = true

    map?.triggerRepaint()
  }, [previewPosition, dims, map])

  return null
}
